import { useEffect, useRef, useState, useCallback } from 'react'

interface UseInfiniteScrollOptions {
  threshold?: number
  rootMargin?: string
  initialPage?: number
  enabled?: boolean
}

const areItemsShallowEqual = <T,>(left: T[], right: T[]) => {
  if (left === right) return true
  if (left.length !== right.length) return false

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false
  }

  return true
}

export function useInfiniteScroll<T>(
  items: T[],
  itemsPerPage: number = 20,
  options: UseInfiniteScrollOptions = {}
) {
  const { threshold = 0.5, rootMargin = '100px', initialPage = 1, enabled = true } = options
  const getNormalizedInitialPage = useCallback(() => {
    const maxPage = Math.max(1, Math.ceil(items.length / itemsPerPage))
    return Math.min(Math.max(1, initialPage), maxPage)
  }, [initialPage, items.length, itemsPerPage])
  const [displayedItems, setDisplayedItems] = useState<T[]>(() => {
    const nextPage = getNormalizedInitialPage()
    return items.slice(0, nextPage * itemsPerPage)
  })
  const [page, setPage] = useState(() => getNormalizedInitialPage())
  const [hasMore, setHasMore] = useState(() => {
    const nextPage = getNormalizedInitialPage()
    return nextPage * itemsPerPage < items.length
  })
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const observerTarget = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const nextPage = getNormalizedInitialPage()
    const nextEndIndex = nextPage * itemsPerPage
    const nextDisplayedItems = items.slice(0, nextEndIndex)
    setDisplayedItems((current) => (
      areItemsShallowEqual(current, nextDisplayedItems) ? current : nextDisplayedItems
    ))
    setPage((current) => current === nextPage ? current : nextPage)
    setHasMore((current) => {
      const nextHasMore = nextEndIndex < items.length
      return current === nextHasMore ? current : nextHasMore
    })
  }, [getNormalizedInitialPage, items, itemsPerPage])

  const loadMore = useCallback(() => {
    setIsLoadingMore(true)

    setTimeout(() => {
      const nextPage = page + 1
      const endIndex = nextPage * itemsPerPage
      setDisplayedItems(items.slice(0, endIndex))
      setPage(nextPage)
      setHasMore(endIndex < items.length)
      setIsLoadingMore(false)
    }, 300)
  }, [items, page, itemsPerPage])

  useEffect(() => {
    if (!enabled) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore()
        }
      },
      {
        threshold,
        rootMargin,
      }
    )

    const currentTarget = observerTarget.current
    if (currentTarget) {
      observer.observe(currentTarget)
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget)
      }
    }
  }, [enabled, hasMore, loadMore, threshold, rootMargin, isLoadingMore])

  return {
    displayedItems,
    hasMore,
    page,
    observerTarget,
    isLoadingMore,
  }
}
